/**
 * ASCII Art Generator - export utilities
 *
 * Shared helpers used by HTML+JS and React exporters.
 */

import type { EffectsState, EffectType } from '../effects/types'

export { getRuntimeContent } from './runtime-content'
export { FALLBACK_RUNTIME } from './runtime-fallback'

// ============================================
// Image helpers
// ============================================

export interface ImageDimensions {
  width: number
  height: number
}

/**
 * Return best-effort dimensions for the source image.
 */
export function getImageDimensions(sourceImage: HTMLImageElement | string): ImageDimensions {
  if (typeof sourceImage === 'string') {
    return { width: 800, height: 600 }
  }

  return {
    width: sourceImage.width || 800,
    height: sourceImage.height || 600,
  }
}

/**
 * Convert the source image to an embeddable JPEG data URL.
 */
export async function imageToDataURL(
  sourceImage: HTMLImageElement | string,
  quality: number = 0.92
): Promise<string> {
  if (typeof sourceImage === 'string') {
    if (sourceImage.startsWith('data:')) {
      return sourceImage
    }

    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = sourceImage
      })
      return imageElementToDataURL(img, quality)
    } catch (error) {
      console.warn('[utils] Failed to load external image:', error)
      return createPlaceholderDataUrl()
    }
  }

  return imageElementToDataURL(sourceImage, quality)
}

function imageElementToDataURL(img: HTMLImageElement, quality: number): string {
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return createPlaceholderDataUrl()
  }

  ctx.drawImage(img, 0, 0)
  return canvas.toDataURL('image/jpeg', quality)
}

function createPlaceholderDataUrl(): string {
  const canvas = document.createElement('canvas')
  canvas.width = 800
  canvas.height = 600
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return ''
  }

  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, 800, 600)
  return canvas.toDataURL('image/jpeg', 0.92)
}

// ============================================
// Effects helpers
// ============================================

/**
 * Extract enabled effects in declared order plus their config payload.
 */
export function extractEnabledEffects(effectsState: EffectsState): {
  enabledEffects: EffectType[]
  effectsConfig: Record<string, unknown>
} {
  const enabledEffects: EffectType[] = []
  const effectsConfig: Record<string, unknown> = {}
  const order = effectsState.order || [
    'vignette', 'noise', 'waves', 'intervals',
    'glitch', 'crt', 'matrix', 'beam',
  ]

  for (const effectType of order) {
    const effect = effectsState[effectType]
    if (effect && effect.enabled) {
      enabledEffects.push(effectType)
      effectsConfig[effectType] = effect.config
    }
  }

  return { enabledEffects, effectsConfig }
}
